import {
  ARROW_DIR,
  type Dir,
  flickerSolid,
  hasKeyTile,
  type Level,
  posKey,
  type RunState,
} from '@xox/shared'

/** Full-cell backgrounds. */
type TileBase = 'ice' | 'rock' | 'flicker-solid'
/** Drawn on top of the base. */
type TileOverlay =
  | 'sheen'
  | 'hole'
  | 'crack'
  | 'arrow'
  | 'flicker-ghost'
  | 'key'
  | 'door'
  | 'door-locked'
  | 'door-open'
type TileSprite = TileBase | TileOverlay
type Rotation = 0 | 90 | 180 | 270

export interface TileLook {
  base: TileBase
  overlay?: TileOverlay
  /** Clockwise rotation of the overlay (arrows). */
  rotate?: Rotation
}

/** Everything about the viewer's run that changes how tiles are drawn. */
export interface TileCtx {
  moves: number
  cracked: ReadonlySet<number>
  hasKey: boolean
  /** Door is locked for this viewer (level has a key they have not picked up). */
  needsKey: boolean
  doorReached: boolean
}

export function tileCtx(
  level: Level,
  run: RunState | null | undefined,
  doorReached: boolean,
): TileCtx {
  return {
    moves: run?.moves ?? 0,
    cracked: new Set(run?.cracked ?? []),
    hasKey: run?.hasKey ?? false,
    needsKey: hasKeyTile(level) && !run?.hasKey,
    doorReached,
  }
}

const ARROW_ROTATE: Record<Dir, Rotation> = { up: 0, right: 90, down: 180, left: 270 }

/** Pure: engine tile + run state → which sprites a cell shows. */
export function spriteFor(tile: string, r: number, c: number, ctx: TileCtx): TileLook {
  switch (tile) {
    case '#':
      return { base: 'rock' }
    case 'o':
      return { base: 'ice', overlay: 'hole' }
    case '~':
      return { base: 'ice', overlay: ctx.cracked.has(posKey({ r, c })) ? 'hole' : 'crack' }
    case '=':
      return flickerSolid(ctx.moves)
        ? { base: 'flicker-solid' }
        : { base: 'ice', overlay: 'flicker-ghost' }
    case 'K':
      return ctx.hasKey ? { base: 'ice' } : { base: 'ice', overlay: 'key' }
    case 'D':
      return {
        base: 'ice',
        overlay: ctx.doorReached ? 'door-open' : ctx.needsKey ? 'door-locked' : 'door',
      }
    default: {
      const dir = ARROW_DIR[tile as keyof typeof ARROW_DIR]
      if (dir) return { base: 'ice', overlay: 'arrow', rotate: ARROW_ROTATE[dir] }
      // '.', 'S' and anything unknown: plain ice with a sparkle on every third diagonal.
      return (r + c) % 3 === 0 ? { base: 'ice', overlay: 'sheen' } : { base: 'ice' }
    }
  }
}

const MINI_FILL: Record<TileSprite, string> = {
  ice: 'var(--ice-surface)',
  rock: 'var(--ice-rock)',
  'flicker-solid': 'var(--ice-rock-light)',
  sheen: 'var(--ice-surface)',
  hole: 'var(--ice-hole)',
  crack: 'var(--ice-surface-deep)',
  arrow: 'var(--ice-arrow)',
  'flicker-ghost': 'var(--ice-rock-light)',
  key: 'var(--ice-brass)',
  door: 'var(--ice-wood)',
  'door-locked': 'var(--ice-wood)',
  'door-open': 'var(--ice-glow)',
}

const MINI_CTX: TileCtx = {
  moves: 0,
  cracked: new Set(),
  hasKey: false,
  needsKey: false,
  doorReached: false,
}

/** One CSS color per tile for the level-select thumbnails, from the same palette as the board. */
export function miniFill(tile: string): string {
  if (tile === 'S') return 'var(--ice-glow)'
  const look = spriteFor(tile, 0, 1, MINI_CTX)
  return MINI_FILL[look.overlay ?? look.base]
}
